/**
 * Community Audit Production Status Component
 *
 * Enhanced design displaying audit history with timerange information,
 * matching the visual style of other audit panels.
 */

import { Shimmer } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import { ProgressBar } from '../../core/components';

import * as styles from './CommunityAuditProductionStatus.css';
import * as shimmerStyles from './shared/shimmer.css';

interface ComprehensiveAnalytics {
  lastAuditDate?: string | null;
  confidence?: string | null;
  totalReports: number;
  auditSessions: number;
  itemsAudited: number;
}

interface AuditSession {
  id: string;
  status: string;
  createdAt: string;
  completedAt?: string | null;
  sampleParameters?: {
    samplingParameters?: {
      timeframe?: {
        start: string;
        end: string;
      } | null;
    } | null;
  } | null;
}

interface CommunityAuditProductionStatusProps {
  comprehensiveAnalytics?: ComprehensiveAnalytics | null;
  auditSessions?: AuditSession[];
  isLoading?: boolean;
}

// Helper function to format date range
function formatDateRange(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const formatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: startDate.getFullYear() !== endDate.getFullYear() ? 'numeric' : undefined,
  };

  return `${startDate.toLocaleDateString('en-US', formatOptions)} - ${endDate.toLocaleDateString('en-US', formatOptions)}`;
}

// Helper function to get the most recent completed audit session
function getLastAuditInfo(sessions?: AuditSession[]) {
  if (!sessions?.length) return null;

  const completedSessions = sessions
    .filter(session => session.completedAt)
    .sort((a, b) => {
      const dateA = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      const dateB = a.completedAt ? new Date(a.completedAt).getTime() : 0;

      return dateA - dateB;
    });

  return completedSessions[0] || null;
}

// Helper function to get active sessions count
function getActiveSessionsCount(sessions?: AuditSession[]): number {
  if (!sessions?.length) return 0;

  return sessions.filter(session => session.status === 'active' || session.status === 'in_progress').length;
}

// Helper function to get confidence as percentage
function getConfidencePercentage(confidence?: string | null): number {
  if (!confidence) return 0;
  const match = confidence.match(/(\d+)/);

  return match ? parseInt(match[1], 10) : 0;
}

// eslint-disable-next-line complexity
export function CommunityAuditProductionStatus({ comprehensiveAnalytics, auditSessions = [], isLoading = false }: CommunityAuditProductionStatusProps) {
  const lastAuditInfo = useMemo(() => getLastAuditInfo(auditSessions), [auditSessions]);
  const activeSessionsCount = useMemo(() => getActiveSessionsCount(auditSessions), [auditSessions]);
  const confidencePercentage = useMemo(() => getConfidencePercentage(comprehensiveAnalytics?.confidence), [comprehensiveAnalytics?.confidence]);

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.header}>
          <Shimmer className={shimmerStyles.shimmerHeader} />
        </div>
        <div className={styles.content}>
          <div className={styles.twoColumnLayout}>
            <div className={styles.leftColumn}>
              <Shimmer className={shimmerStyles.shimmerSectionTitle} />
              {Array.from({ length: 2 }, (_, i) => `shimmer-left-${i}`).map(shimmerKey => (
                <div key={shimmerKey} className={shimmerStyles.shimmerItem}>
                  <Shimmer className={shimmerStyles.shimmerLabel} />
                  <Shimmer className={shimmerStyles.shimmerSmallValue} />
                </div>
              ))}
            </div>
            <div className={styles.rightColumn}>
              <Shimmer className={shimmerStyles.shimmerSectionTitle} />
              <div className={shimmerStyles.shimmerItem}>
                <Shimmer className={shimmerStyles.shimmerLabel} />
                <Shimmer className={shimmerStyles.shimmerSmallValue} />
              </div>
              <Shimmer className={shimmerStyles.shimmerProgressBar} />
            </div>
          </div>
          <div className={styles.bottomSection}>
            <Shimmer className={shimmerStyles.shimmerSectionTitle} />
            {Array.from({ length: 3 }, (_, i) => `shimmer-bottom-${i}`).map(shimmerKey => (
              <div key={shimmerKey} className={shimmerStyles.shimmerItem}>
                <Shimmer className={shimmerStyles.shimmerLabel} />
                <Shimmer className={shimmerStyles.shimmerSmallValue} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // If no analytics data, show N/A state
  if (!comprehensiveAnalytics) {
    return (
      <div className={styles.panel}>
        <div className={styles.header}>
          <span className={styles.title}>Audit History</span>
        </div>
        <div className={styles.content}>
          <div className={styles.statusItem}>
            <span className={styles.statusLabel}>No data available</span>
            <span className={styles.statusValue}>N/A</span>
          </div>
        </div>
      </div>
    );
  }

  const analytics = comprehensiveAnalytics;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>Audit History</span>
      </div>

      <div className={styles.content}>
        {/* Two-Column Layout */}
        <div className={styles.twoColumnLayout}>
          {/* Left Column - Last Audit */}
          <div className={styles.leftColumn}>
            <div className={styles.sectionHeader}>
              <span>LAST AUDIT</span>
            </div>

            <div className={styles.statusItem}>
              <span className={styles.statusLabel}>Completed</span>
              <span className={styles.statusValue}>{analytics.lastAuditDate || 'N/A'}</span>
            </div>

            {lastAuditInfo?.sampleParameters?.samplingParameters?.timeframe && (
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Period</span>
                <span className={styles.statusValue}>
                  {formatDateRange(
                    lastAuditInfo.sampleParameters.samplingParameters.timeframe.start,
                    lastAuditInfo.sampleParameters.samplingParameters.timeframe.end
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Right Column - Confidence Level */}
          <div className={styles.rightColumn}>
            <div className={styles.sectionHeader}>
              <span>CONFIDENCE LEVEL</span>
            </div>

            {analytics.confidence ? (
              <div className={styles.progressItem}>
                <div className={styles.progressHeader}>
                  <span className={styles.statusLabel}>Current</span>
                  <span className={styles.statusValue}>{analytics.confidence}</span>
                </div>
                <ProgressBar aria-label={`Confidence level: ${analytics.confidence}`} color="success" size="panel" value={confidencePercentage} />
              </div>
            ) : (
              <div className={styles.statusItem}>
                <span className={styles.statusLabel}>Current</span>
                <span className={styles.statusValue}>N/A</span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Section - Activity Summary */}
        <div className={styles.bottomSection}>
          <div className={styles.sectionHeader}>
            <span>ACTIVITY SUMMARY</span>
          </div>

          <div className={styles.activityGrid}>
            <div className={styles.activityItem}>
              <span className={styles.activityLabel}>Reports</span>
              <span className={styles.activityValue}>{analytics.totalReports || 'N/A'}</span>
            </div>

            <div className={styles.activityItem}>
              <span className={styles.activityLabel}>Sessions</span>
              <span className={styles.activityValue}>
                {activeSessionsCount}/{analytics.auditSessions || 0}
              </span>
            </div>

            <div className={styles.activityItem}>
              <span className={styles.activityLabel}>Items Audited</span>
              <span className={styles.activityValue}>{analytics.itemsAudited ? analytics.itemsAudited.toLocaleString() : 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
